import { useMemo, useState } from 'react';
import { Alert, Box, Grid, MenuItem, TextField } from '@mui/material';

import {
  pickDefaultLoanProgram,
  USER_LOAN_ESTIMATION_DICT,
  USER_LOAN_ESTIMATION_TYPE,
  USER_LOAN_PROGRAM,
  USER_OCCUPANCY,
  USER_ROLE,
  userLoanEstimationTypes,
  userLoanPrograms,
  userOccupancys,
  userRoles,
} from '@/config/constants';
import { useCreateUserByAdminMutation } from '@/apis/extended/admin/userApi';
import { CommonDialog } from '@/components/Common/CommonDialog';
import { CommonFormControl } from '@/components/Common/CommonFormControl';

type InviteFormState = {
  email: string;
  password: string;
  name: string;
  phone: string;
  role: USER_ROLE;
  occupancy: USER_OCCUPANCY;
  loanEstimationType: USER_LOAN_ESTIMATION_TYPE;
  loanProgram: USER_LOAN_PROGRAM;
  monthlyIncome: string;
  creditScore: string;
  downPayment: string;
  monthlyDebt: string;
  loanTerm: string;
};

const initialForm: InviteFormState = {
  email: '',
  password: '',
  name: '',
  phone: '',
  role: USER_ROLE.USER,
  occupancy: USER_OCCUPANCY.DEFAULT,
  loanEstimationType: USER_LOAN_ESTIMATION_TYPE.HOME_BUYER,
  loanProgram: USER_LOAN_PROGRAM.CONVENTIONAL,
  monthlyIncome: '',
  creditScore: '',
  downPayment: '',
  monthlyDebt: '',
  loanTerm: '',
};

type InviteUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const InviteUserModal = ({ isOpen, onClose }: InviteUserModalProps) => {
  const [form, setForm] = useState({ ...initialForm });
  const [message, setMessage] = useState<string | null>(null);
  const [createUser, { isLoading }] = useCreateUserByAdminMutation();

  const isValid = useMemo(() => {
    return !!form.email && !!form.password && !!form.name && !!form.role;
  }, [form]);

  const filteredInviteLoanPrograms = useMemo(() => {
    const allowed =
      USER_LOAN_ESTIMATION_DICT[form.loanEstimationType]?.LOAN_PROGRAMS || [];
    return userLoanPrograms.filter((p) => allowed.includes(p.value));
  }, [form.loanEstimationType]);

  const resetForm = () => {
    setForm({ ...initialForm });
  };

  const handleClose = () => {
    resetForm();
    setMessage(null);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone.trim(),
        role: form.role,
        occupancy: form.occupancy,
        loanEstimationType: form.loanEstimationType,
        loanProgram: form.loanProgram,
        monthlyIncome: form.monthlyIncome
          ? Number(form.monthlyIncome)
          : undefined,
        creditScore: form.creditScore ? Number(form.creditScore) : undefined,
        downPayment: form.downPayment ? Number(form.downPayment) : undefined,
        monthlyDebt: form.monthlyDebt ? Number(form.monthlyDebt) : undefined,
        loanTerm: form.loanTerm ? Number(form.loanTerm) : undefined,
      };
      await createUser(payload).unwrap();
      setMessage('Invite sent and user created.');
      resetForm();
    } catch (err: any) {
      setMessage(err?.data?.message || 'Failed to create user invite.');
    }
  };

  return (
    <CommonDialog
      isOpen={isOpen}
      title="Invite User"
      icon="fa-light fa-user-plus"
      closeButtonText="Close"
      confirmButtonText="Send Invite"
      onClose={handleClose}
      onConfirm={handleSubmit}
      isConfirmDisabled={!isValid}
      isLoading={isLoading}
      sx={{ minWidth: 600 }}
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        {message && <Alert severity="info">{message}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Full Name:">
              <TextField
                fullWidth
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Email:">
              <TextField
                fullWidth
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Phone:">
              <TextField
                fullWidth
                value={form.phone}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Initial Password:">
              <TextField
                fullWidth
                type="text"
                value={form.password}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, password: e.target.value }))
                }
              />
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Role:">
              <TextField
                select
                fullWidth
                value={form.role}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    role: e.target.value as USER_ROLE,
                  }))
                }
              >
                {userRoles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </TextField>
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Occupancy:">
              <TextField
                select
                fullWidth
                value={form.occupancy}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    occupancy: e.target.value as USER_OCCUPANCY,
                  }))
                }
              >
                {userOccupancys.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Loan Estimation Type:">
              <TextField
                select
                fullWidth
                value={form.loanEstimationType}
                onChange={(e) => {
                  const loanEstimationType = e.target
                    .value as USER_LOAN_ESTIMATION_TYPE;
                  const allowed =
                    USER_LOAN_ESTIMATION_DICT[loanEstimationType]
                      ?.LOAN_PROGRAMS || [];
                  setForm((prev) => {
                    const nextProgram = allowed.includes(prev.loanProgram)
                      ? prev.loanProgram
                      : pickDefaultLoanProgram(allowed);
                    return {
                      ...prev,
                      loanEstimationType,
                      loanProgram: nextProgram,
                    };
                  });
                }}
              >
                {userLoanEstimationTypes.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <CommonFormControl label="Loan Program:">
              <TextField
                select
                fullWidth
                value={form.loanProgram}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    loanProgram: e.target.value as USER_LOAN_PROGRAM,
                  }))
                }
              >
                {filteredInviteLoanPrograms.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </CommonFormControl>
          </Grid>
        </Grid>

        <Box sx={{ fontWeight: 600 }}>Loan Info (optional)</Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <CommonFormControl label="Monthly Income:">
              <TextField
                fullWidth
                type="number"
                value={form.monthlyIncome}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    monthlyIncome: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <CommonFormControl label="Credit Score:">
              <TextField
                fullWidth
                type="number"
                value={form.creditScore}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    creditScore: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <CommonFormControl label="Down Payment:">
              <TextField
                fullWidth
                type="number"
                value={form.downPayment}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    downPayment: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <CommonFormControl label="Monthly Debt:">
              <TextField
                fullWidth
                type="number"
                value={form.monthlyDebt}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    monthlyDebt: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <CommonFormControl label="Loan Term (years):">
              <TextField
                fullWidth
                type="number"
                value={form.loanTerm}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    loanTerm: e.target.value,
                  }))
                }
              />
            </CommonFormControl>
          </Grid>
        </Grid>
      </Box>
    </CommonDialog>
  );
};
